/**
 * @file lib/api-with-outbox.ts
 * @description 統一 POST/PATCH 線上/離線分支。線上直發 apiClient，離線推 PWA outbox。
 *
 * 用法：
 *   const result = await postWithOutbox('/api/x', body, { entityType: 'sample' });
 *   if ((result as { offline?: boolean }).offline) {
 *     message.success('已加入離線佇列，連線後自動同步');
 *   } else {
 *     message.success('已建立');
 *   }
 */

import { apiClient } from '@/lib/api-client';
import { pushOutboxReliable } from '@/pwa/db';

/** postWithOutbox / patchWithOutbox 的可選參數 */
interface Options {
  /** 關聯實體類型，例如 'sample' / 'audit-finding' / 'audit-task' */
  entityType?: string;
  /** 關聯實體本地臨時 ID（離線新建時尚無服務端 ID） */
  entityClientLocalId?: string;
  /** 關聯稽核任務 ID，用於離線管理頁按任務統計 */
  taskId?: number;
  /** 關聯案件 ID，用於離線管理頁按案件統計 */
  caseId?: number;
}

/**
 * 線上時直發 POST，離線時推 outbox。
 * @param url - API 路徑
 * @param body - 請求 body
 * @param opts - 關聯實體資訊（離線排隊時記錄到 outbox 條目）
 * @returns 線上：API response（型別 T）；離線：`{ offline: true }`
 */
export async function postWithOutbox<T>(
  url: string,
  body: unknown,
  opts: Options = {},
): Promise<T | { offline: true }> {
  if (navigator.onLine) {
    return apiClient.post<T>(url, body);
  }
  await pushOutboxReliable({ method: 'POST', url, body, ...opts });
  return { offline: true };
}

/**
 * 線上時直發 PATCH，離線時推 outbox。
 * @param url - API 路徑
 * @param body - 請求 body
 * @param opts - 關聯實體資訊（離線排隊時記錄到 outbox 條目）
 * @returns 線上：API response（型別 T）；離線：`{ offline: true }`
 */
export async function patchWithOutbox<T>(
  url: string,
  body: unknown,
  opts: Options = {},
): Promise<T | { offline: true }> {
  if (navigator.onLine) {
    return apiClient.patch<T>(url, body);
  }
  await pushOutboxReliable({ method: 'PATCH', url, body, ...opts });
  return { offline: true };
}
